import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { useLang } from "@/i18n";
import { PRIVACY } from "@/privacy";

const PRIVACY_EMAIL = "privacy@restobreda.com";

export default function PrivacyPolicy() {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const p = PRIVACY[lang] || PRIVACY.fr;

  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener("open-privacy-policy", openHandler);
    return () => window.removeEventListener("open-privacy-policy", openHandler);
  }, []);

  return (
    <>
      <button
        type="button"
        data-testid="footer-privacy-link"
        onClick={() => setOpen(true)}
        className="text-xs text-paper/60 underline decoration-paper/30 underline-offset-4 transition-colors duration-300 hover:text-wood-light"
      >
        {p.link}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="privacy-page"
            className="fixed inset-0 z-[110] bg-paper"
            role="dialog"
            aria-modal="true"
            aria-label={p.title}
          >
            <div data-lenis-prevent className="h-full overflow-y-auto">
              <div className="mx-auto max-w-3xl px-5 py-10 md:px-8 md:py-16">
                <button
                  type="button"
                  data-testid="privacy-back"
                  onClick={() => setOpen(false)}
                  className="group inline-flex items-center gap-2 rounded-full border border-pine/15 px-5 py-2.5 text-sm font-semibold text-pine transition-all duration-300 hover:border-pine hover:bg-pine hover:text-paper"
                >
                  <ArrowLeft size={16} className="transition-transform duration-300 group-hover:-translate-x-1" />
                  {p.back}
                </button>

                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div className="mt-12 flex items-center gap-3">
                    <ShieldCheck size={20} className="text-wood" />
                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-wood">
                      {p.updated}
                    </p>
                  </div>
                  <h1 className="mt-4 font-serif text-4xl font-light tracking-tight text-pine md:text-5xl">
                    {p.title}
                  </h1>
                </motion.div>

                <div className="mt-12 space-y-10">
                  {p.sections.map((section, i) => (
                    <motion.section
                      key={i}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.2 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <h2 className="font-serif text-2xl tracking-tight text-pine">{section.h}</h2>
                      <p className="mt-3 text-base leading-relaxed text-pine/75">{section.p}</p>
                    </motion.section>
                  ))}
                </div>

                <div className="mt-12 rounded-2xl bg-cream p-6 md:p-8">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-pine/50">
                    {p.contactLabel}
                  </p>
                  <p className="mt-2 text-base text-pine/80">{p.contactText}</p>
                  <a
                    href={`mailto:${PRIVACY_EMAIL}`}
                    data-testid="privacy-email"
                    className="mt-3 inline-block font-serif text-2xl tracking-tight text-pine underline decoration-wood/50 underline-offset-8 transition-colors hover:text-wood"
                  >
                    {PRIVACY_EMAIL}
                  </a>
                </div>

                <div className="h-16" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
